import React from "react";
import { graphql } from "react-relay";

import { faEllipsisV } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import moment from "moment";
import { ConnectionHandler } from "relay-runtime";

import { AppContext } from "~/common";
import { confirm } from "~/lib/prompts";
import { commitMutationPromise } from "~/lib/relayUtil";
import { environment } from "~/models";
import { FeedbackContext } from "~/nav/panels/Feedback";
import IconButton from "~/ui/legacy/IconButton";
import Menu from "~/ui/menu";
import toast from "~/ui/toast";

import type { MenuMarkAllAsReadMutation } from "~/__generated__/MenuMarkAllAsReadMutation.graphql";

import styles from "./FeedbackPanel.module.css";

const markAllAsReadMutation = graphql`
	mutation MenuMarkAllAsReadMutation {
		feedbackMarkAllAsRead {
			updatedCount
		}
	}
`;

export default function KebabMenu({ unreadCount }: { unreadCount: number }) {
	const { view, type, about, from } = React.useContext(FeedbackContext);
	const { openSettingsModal } = React.useContext(AppContext);
	const openNotificationSettings = React.useCallback(() => openSettingsModal("notifications"), [openSettingsModal]);

	const onMarkAsRead = React.useCallback(async () => {
		if (
			await confirm(
				<p>
					This will mark{" "}
					<b>
						{unreadCount} item{unreadCount > 1 ? "s" : ""}
					</b>{" "}
					as read.
				</p>,
				{ title: "Are you sure?" },
			)
		) {
			commitMutationPromise<MenuMarkAllAsReadMutation>(environment, {
				mutation: markAllAsReadMutation,
				variables: {},
				onCompleted: ({ feedbackMarkAllAsRead }, err) => {
					if (err?.length || !feedbackMarkAllAsRead) {
						toast.error(err?.[0]?.message || "Failed to mark items as read");
					} else {
						toast.success(
							`${feedbackMarkAllAsRead.updatedCount} item${
								feedbackMarkAllAsRead.updatedCount > 1 ? "s" : ""
							} marked as read!`,
						);
					}
				},
				updater: store => {
					/*
					 * Note: we use this approach to avoid refetching data from the backend and also to prevent
					 *       mutation from returning ALL updated instances.
					 *       This way we only update the ones visible to the user.
					 */

					const field = "lastSeenByUser";
					const value = moment.utc().format();
					const viewer = store.get("viewer");

					if (!viewer) return;

					// First, let's update the unread count
					viewer.setValue(0, "feedbackUnreadItemsCount");

					// Then, let's remove unread indicators
					const variables = { category: view, feedbackType: type, aboutUserId: about, fromUserId: from };
					const connection = ConnectionHandler.getConnection(viewer, "FeedbackList_feedbacks", variables);

					if (!connection) return;

					const nodes = (connection.getLinkedRecords("edges") ?? []).map(e => e.getLinkedRecord("node"));

					nodes.forEach(node => node?.setValue(value, field));
				},
			});
		}
	}, [unreadCount, view, type, about, from]);

	return (
		<Menu>
			<Menu.Trigger>
				<IconButton title="Options" icon={faEllipsisV} className={styles.kebabMenu} />
			</Menu.Trigger>
			<Menu.Content>
				<Menu.Item onSelect={onMarkAsRead}>Mark all as read</Menu.Item>
				<Menu.Item onSelect={openNotificationSettings}>Feedback notifications</Menu.Item>
			</Menu.Content>
		</Menu>
	);
}
